import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  TypePolicies,
  from,
} from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import Bugsnag from "@bugsnag/js";
import * as ActionCable from "@rails/actioncable";
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink";
import { typeDefs } from "./typeDefs";
import { isNewDashboardVar } from "./cache";
import {
  isHandledAgencyLifecycleRefusal,
  isHandledBillingCompanyVatRefusal,
  isHandledChatRefusal,
  isHandledCreateUserDuplicate,
  isHandledReissueRefusal,
} from "./handledErrors";
import { graphQLBaseUrl, webSocketBaseUrl } from "src/config";
import { store } from "src/models";
import { isTrackingDisabled } from "src/utils/tracking";

const ACCESS_TOKEN_KEY = "Contact-Access-Token";
const LAST_FRONTEND_DEPLOY_TIMESTAMP_KEY = "Contact-Last-Frontend-Deploy";

const cableLink = new ActionCableLink({
  cable: ActionCable.createConsumer(webSocketBaseUrl),
  connectionParams: () => ({
    access_token: store.getState().currentUser.accessToken,
  }),
});

const httpLink = new HttpLink({
  uri: graphQLBaseUrl,
});

const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;

    if (headers) {
      const accessToken = headers.get(ACCESS_TOKEN_KEY);
      if (accessToken) {
        store.getActions().currentUser.setAccessToken(accessToken);
      }

      const lastFrontendDeployTimestamp = headers.get(
        LAST_FRONTEND_DEPLOY_TIMESTAMP_KEY,
      );
      if (lastFrontendDeployTimestamp) {
        store
          .getActions()
          .contactConfig.setLastFrontendDeployTimestamp(
            lastFrontendDeployTimestamp,
          );
      }
    }

    return response;
  });
});

const authLink = new ApolloLink((operation, forward) => {
  const token = store.getState().currentUser.accessToken;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  operation.setContext(({ headers }: Record<string, any>) => ({
    headers: {
      ...headers,
      [ACCESS_TOKEN_KEY]: token,
    },
  }));
  return forward(operation);
});

const errorReportingLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (
      response.errors?.some(
        (error) => error.extensions?.code === "TERMS_ACCEPTANCE_REQUIRED",
      )
    ) {
      window.dispatchEvent(new Event("contact:terms-required"));
    }
    const error = response.errors?.find(
      (candidate) =>
        !isHandledBillingCompanyVatRefusal(
          operation.operationName,
          candidate,
        ) &&
        !isHandledCreateUserDuplicate(operation.operationName, candidate) &&
        !isHandledChatRefusal(operation.operationName, candidate) &&
        !isHandledReissueRefusal(operation.operationName, candidate) &&
        !isHandledAgencyLifecycleRefusal(operation.operationName, candidate),
    );
    if (error && !isTrackingDisabled()) {
      Bugsnag.notify(new Error(error.message), (event) => {
        event.severity = "error";
        event.unhandled = true;
        event.groupingHash = `GraphQL Error Response: ${error.message}`;
        if (error.path) {
          event.addMetadata("gql", { path: error.path[0] });
        }
      });
    }

    return response;
  });
});

const hasSubscriptionOperation = ({ query: { definitions } }: any) => {
  return definitions.some(
    ({ kind, operation }: any) =>
      kind === "OperationDefinition" && operation === "subscription",
  );
};

// One set of cache policies for both clients, so the signed-in client the app
// swaps to at login can no longer drift from the signed-out one.
const typePolicies: TypePolicies = {
  User: {
    fields: {
      jobs: relayStylePagination(["workspace", "filters", "sortOrder"]),
      activityStream: relayStylePagination(),
    },
  },
  BookerProfile: {
    fields: {
      savedTalent: relayStylePagination(["sortOrder"]),
      savedTalentCollections: relayStylePagination(["sortOrder"]),
      activityStream: relayStylePagination(),
    },
  },
  SavedTalentCollection: {
    fields: {
      members: relayStylePagination(["sortOrder"]),
    },
  },
  Query: {
    queryType: true,
    fields: {
      talentSearch: relayStylePagination(),
      verticalTalentSearch: relayStylePagination([
        "workspace",
        "group",
        "isContactOpen",
        "isContactSelect",
      ]),
      bookers: relayStylePagination(["sortField, sortDirection", "companyId"]),
      talentApplications: relayStylePagination([
        "nameOrEmail",
        "state",
        "sortField",
        "sortDirection",
      ]),
      invoices: relayStylePagination(["filters", "sortOrder"]),
      thirdPartyAgencies: relayStylePagination(),
      bookingCompanies: relayStylePagination(),
      isNewDashboard: {
        read() {
          return isNewDashboardVar();
        },
      },
    },
  },
  JobTalent: {
    fields: {
      outboundPayments: {
        merge: false,
      },
    },
  },
  Agency: {
    fields: {
      agents: relayStylePagination(["sortOrder"]),
      agencyReviews: relayStylePagination(["sortOrder"]),
      packages: relayStylePagination(["sortOrder"]),
      commissionRateHistory: relayStylePagination(["sortOrder"]),
      jobs: relayStylePagination(),
    },
  },
};

export const graphQLClient = new ApolloClient({
  link: ApolloLink.split(
    hasSubscriptionOperation,
    cableLink,
    from([authLink, errorReportingLink, afterwareLink, httpLink]),
  ),
  cache: new InMemoryCache({
    typePolicies,
  }),
  typeDefs,
});

export const graphQLClientWithCableLink = () => {
  const splitLink = ApolloLink.split(
    hasSubscriptionOperation,
    cableLink,
    from([authLink, errorReportingLink, afterwareLink, httpLink]),
  );

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      typePolicies,
    }),
    typeDefs,
  });
};
